import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useNxSocket } from "@blpde/connect";
import { useAccountConfig } from "@blpde/connect/config";
import {
  buildAuthHeaders,
  getApiOrigin,
  handleAuthFailure,
  hasAuthCredentials,
  tryRefreshAccessToken,
} from "@/auth";
import {
  notificationDeviceSettingFor,
  SHELL_DEVICE_TYPE,
} from "@/notificationDeviceSettings";
import { fetchLoadedRange } from "@/pagedRange";
import { sessionCompanyID } from "@/scope";

/** The API call an action performs: `method` on `path` of a module's public API. */
export interface NotificationActionRequest {
  /** The key of the module whose API is called (`chat`). */
  module: string;
  /** POST, PUT, PATCH or DELETE. */
  method: string;
  /** The path inside the module's public API (`/v1/channels/5/…`). */
  path: string;
  /** The JSON body of the call; none without. */
  body?: unknown;
}

/**
 * One button a notification offers besides opening it: it opens something
 * (`link`) or calls a module's API (`request`) — exactly one of the two.
 */
export interface NotificationAction {
  /** Unique inside its notification; what `resolvedActionKey` names. */
  key: string;
  /** The English fallback caption. */
  label?: string;
  /** The caption's i18n key in the sending app's message table. */
  labelKey?: string;
  /**
   * How the action reads once taken, in the past tense („Freigegeben“): the
   * English fallback, and its i18n key in the same table as `labelKey`.
   * Without either the action's own caption is shown.
   */
  resolvedLabel?: string;
  resolvedLabelKey?: string;
  style?: "" | "primary" | "danger";
  /** An app-neutral link (see `link` of the notification). */
  link?: string;
  request?: NotificationActionRequest;
}

export interface MasterNotification {
  id: number;
  uuid?: string;
  moduleID?: number;
  moduleKey?: string;
  type?: string;
  title?: string;
  titleKey?: string;
  body?: string;
  bodyKey?: string;
  /**
   * What the notification opens: app-neutral
   * `<module key>/<resource type>/<resource id>[?query]`, or a web path with
   * a leading `/` from an older producer (`notificationLinks.ts`).
   */
  link?: string;
  severity?: string;
  data?: unknown;
  groupCount?: number;
  /** The master company it belongs to; 0 = none. */
  companyID?: number;
  resourceType?: string;
  resourceID?: string;
  /** What a mute of it refers to (`channel` + the channel's id). */
  contextType?: string;
  contextID?: string;
  senderID?: number;
  senderAddress?: string;
  senderName?: string;
  priority?: string;
  actions?: NotificationAction[] | null;
  /**
   * When the notification is (or becomes) visible, and its place in the
   * inbox sort. After the last write (`updatedAt`) it is scheduled or
   * snoozed: not in the inbox yet.
   */
  notifyAt?: string;
  snoozedUntil?: string | null;
  expiresAt?: string | null;
  /** Dealt with: it offers no actions any more. */
  resolvedAt?: string | null;
  /** The action it was dealt with through, if it was one of its own. */
  resolvedActionKey?: string;
  changeRevision?: number;
  deletedAt?: string | null;
  readAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/** One entry of master's notification type register (`/notifications/types`). */
export interface NotificationType {
  /** `<module key>.<name>` (`mail.received`); master's own start with `master.`. */
  key: string;
  moduleID: number;
  moduleKey: string;
  /** The English fallback texts. */
  label: string;
  description: string;
  /** The texts' i18n keys in the declaring app's message table. */
  titleKey: string;
  descriptionKey: string;
  /** What a mute of this type's notifications refers to; empty = none. */
  contextType: string;
  defaultPriority: string;
  enabledByDefault: boolean;
  /** Notifies even in a muted context. */
  muteExempt: boolean;
  sortOrder: number;
}

interface ListResponse {
  notifications?: MasterNotification[];
  unreadCount?: number;
}

interface CountResponse {
  count?: number;
  /** Unread per producing module, by module key; master's own under `master`. */
  modules?: Record<string, number>;
}

interface RowResponse {
  status?: string;
  notification?: MasterNotification | null;
}

/** The key master counts its own notifications under (`unread-count.modules`). */
export const MASTER_MODULE_KEY = "master";

/** Rows per page of the bell's list; master caps a page at 200. */
const PAGE_SIZE = 50;
/** master's cap on one list request and on the ids of one bulk request. */
const MAX_PAGE_SIZE = 200;

/**
 * The bell's list: unread notifications only, newest activity first. A
 * notification leaves it once it is read.
 */
const items = ref<MasterNotification[]>([]);
const unreadCount = ref(0);
/**
 * Unread per producing module (module key → count; master's own under
 * `master`), as master's unread count answers it. Between two answers the
 * bell's own changes are applied to it.
 */
const unreadByModule = ref<Record<string, number>>({});
const loading = ref(false);
const loaded = ref(false);
const error = ref<string | null>(null);
/** Master has more unread notifications than the list holds. */
const hasMore = ref(false);

let listInFlight: Promise<void> | null = null;
let moreInFlight: Promise<void> | null = null;
/**
 * The last row of master's last answer: the keyset cursor for the next page.
 * Not the list's last row — the list re-sorts by millisecond while master
 * sorts by microsecond, so the two can disagree within a millisecond.
 */
let cursorRow: MasterNotification | null = null;
let pollTimer: ReturnType<typeof setTimeout> | null = null;
let pollAbort: AbortController | null = null;
/**
 * Poller singleton state. Consumers are ShellNav (the bell's count) and the
 * bell's panel while it is open, and their mounts can overlap: a remount
 * mounts the NEW consumer before the OLD one unmounts. A plain boolean plus a
 * single AbortController slot let such an overlap spawn a second poll chain
 * and orphan the first one's 60s long poll. Orphans pile up and
 * exhaust the browser's per-origin connection pool (six on HTTP/1.1), which
 * stalls every other request as "pending". Consumers are refcounted and each
 * chain is bound to a generation token instead.
 */
let consumers = 0;
let pollGen = 0;
let pollRunning = false;

/**
 * The company the bell and the page are scoped to: the one the session works
 * in. Master then answers that company's notifications and the ones that
 * belong to no company (mail, calendar and chat belong to the account). 0:
 * the session has no company — everything, as before. Known once the first
 * request asked.
 * The shell's own company switch reloads the tab; whatever moves the
 * session without one says so (`COMPANY_CHANGED_EVENT`) and the store
 * follows (`followCompanyChange`).
 */
let scopeCompany: number | null = null;

/**
 * The window event that says the session moved to another company without a
 * reload of the shell. The shell's own switch never fires it: it reloads
 * every tab (`scope.ts`).
 */
export const COMPANY_CHANGED_EVENT = "formica:company-changed";

/**
 * The session's company for a request. While master has not said which it
 * is, nothing is loaded — an unscoped answer would show (and count) the
 * other companies' notifications for a moment.
 */
async function requestCompany(): Promise<number> {
  const id = await sessionCompanyID();
  if (id === null) {
    throw new Error("notifications: the session's company is not known");
  }
  scopeCompany = id;
  return id;
}

/**
 * Whether a pushed row belongs to another company than the session's: such a
 * row is neither listed nor counted. A row of no company (0) belongs to
 * every one; a session without a company sees them all.
 */
export function isForeignCompany(
  row: Pick<MasterNotification, "companyID">,
  company: number | null = scopeCompany,
): boolean {
  return !!row.companyID && !!company && row.companyID !== company;
}

function authHeaders(): Headers {
  return buildAuthHeaders({ Accept: "application/json" });
}

/**
 * A path of master's notification routes with the shell's device type:
 * master shows a session what its settings show for its device type — the
 * type of the device the session is bound to, else this parameter.
 */
function withDeviceType(path: string): string {
  return `${path}${path.includes("?") ? "&" : "?"}deviceType=${SHELL_DEVICE_TYPE}`;
}

async function request(
  path: string,
  init: RequestInit = {},
  canRefresh = true,
): Promise<Response> {
  const headers = authHeaders();
  for (const [key, value] of new Headers(init.headers).entries()) {
    headers.set(key, value);
  }
  const res = await fetch(`${getApiOrigin()}/v1${withDeviceType(path)}`, {
    ...init,
    headers,
  });
  if ((res.status === 401 || res.status === 403) && canRefresh) {
    const refreshed = await tryRefreshAccessToken();
    if (refreshed) return request(path, init, false);
    handleAuthFailure();
  }
  if (!res.ok) throw new Error(`notifications request failed (${res.status})`);
  return res;
}

/**
 * A row's place in the inbox: master sorts by `notifyAt` (newest first), the
 * instant a notification became visible. Rows of before that field fall back
 * to their last change.
 */
function sortStamp(row: MasterNotification): string {
  return row.notifyAt || row.updatedAt || row.createdAt || "";
}

function sortItems(rows: MasterNotification[]): MasterNotification[] {
  return [...rows].sort((a, b) => {
    const at = Date.parse(sortStamp(a));
    const bt = Date.parse(sortStamp(b));
    if (Number.isFinite(at) && Number.isFinite(bt) && bt !== at) return bt - at;
    return (b.id || 0) - (a.id || 0);
  });
}

/**
 * Whether a row is scheduled or snoozed — not in the inbox yet. Master's own
 * test: `notifyAt` lies after the row's last write (`updatedAt`). Both are
 * master's clock, so this browser's clock plays no part; only a row without
 * `updatedAt` is measured against it.
 *
 * It holds for a row master just wrote — a push frame, the answer of a
 * snooze. A row of the inbox list is due by definition (master leaves the
 * others out), also one that was snoozed before and has come back unchanged.
 */
export function isNotificationPending(
  row: Pick<MasterNotification, "notifyAt" | "updatedAt">,
  now: number = Date.now(),
): boolean {
  const at = Date.parse(row.notifyAt || "");
  if (!Number.isFinite(at)) return false;
  const written = Date.parse(row.updatedAt || "");
  return at > (Number.isFinite(written) ? written : now);
}

function moduleOf(row: MasterNotification): string {
  return row.moduleKey || MASTER_MODULE_KEY;
}

/** The shell's settings, as master resolves them for this session (loaded at boot). */
const shellConfig = useAccountConfig<Record<string, unknown>>("shell", {
  immediate: false,
});

/**
 * Resolves once the shell's settings are known to this tab: master has
 * answered them, or the read failed and the boot cache (or the defaults)
 * stand. Right after a sign-in the first read may still be on its way, and a
 * decision by `browserSettingOf` before it would use the defaults of a
 * browser instead of the user's settings. Starts the read when none runs.
 */
export async function shellSettingsKnown(): Promise<void> {
  if (shellConfig.loaded.value || !hasAuthCredentials()) return;
  if (!shellConfig.loading.value) {
    await shellConfig.reload().catch(() => undefined);
    return;
  }
  await new Promise<void>((resolve) => {
    const stop = watch(shellConfig.loading, (reading) => {
      if (reading) return;
      stop();
      resolve();
    });
  });
}

/** master's type register by key, once it was read (`notificationTypeRegister`). */
let typeRegister: ReadonlyMap<string, NotificationType> | null = null;
let typeRegisterLoad: Promise<ReadonlyMap<string, NotificationType>> | null =
  null;

/**
 * master's type register by key, read once per page — what tells a type
 * that is off by default. A failed read is tried again on the next call.
 */
export function notificationTypeRegister(): Promise<
  ReadonlyMap<string, NotificationType>
> {
  if (!typeRegisterLoad) {
    typeRegisterLoad = fetchNotificationTypes().then(
      (types) => {
        typeRegister = new Map(types.map((type) => [type.key, type]));
        return typeRegister;
      },
      (err) => {
        typeRegisterLoad = null;
        throw err;
      },
    );
  }
  return typeRegisterLoad;
}

/**
 * What a notification does in this browser by the session's settings
 * (`notificationDeviceSettings.ts`): `off`, `bell` or `system`. A type the
 * register does not hold (yet) counts as on by default.
 */
export function browserSettingOf(
  row: Pick<MasterNotification, "moduleKey" | "type">,
  register: ReadonlyMap<string, NotificationType> | null = typeRegister,
) {
  const type = row.type ?? "";
  return notificationDeviceSettingFor(
    shellConfig.effective.value,
    SHELL_DEVICE_TYPE,
    row.moduleKey || MASTER_MODULE_KEY,
    type,
    register?.get(type)?.enabledByDefault ?? true,
  );
}

/** Applies the bell's own change to the per-module counts until master answers. */
function countModule(row: MasterNotification, delta: number): void {
  const key = moduleOf(row);
  const next = Math.max(0, (unreadByModule.value[key] ?? 0) + delta);
  const counts = { ...unreadByModule.value };
  if (next > 0) counts[key] = next;
  else delete counts[key];
  unreadByModule.value = counts;
}

/**
 * Takes a pushed row into the bell's list. `due`: master says it is visible
 * now (`notification.new` — also a scheduled or snoozed one whose time has
 * come); an update is measured (`isNotificationPending`). master pushes a
 * row only to the sockets whose settings show it; a row this browser's
 * settings turn off (`browserSettingOf`) is left out here as well — for the
 * moment after a change master learns late (made on another master node or
 * by an administrator: up to a minute), as it leaves the row out of the
 * list and the counts.
 */
function upsert(row: MasterNotification, due = false): void {
  if (!row || !row.id) return;
  const idx = items.value.findIndex((item) => item.id === row.id);
  if (
    row.readAt ||
    row.deletedAt ||
    isForeignCompany(row) ||
    browserSettingOf(row) === "off" ||
    (!due && isNotificationPending(row))
  ) {
    // Read elsewhere (another tab), snoozed, another company's or switched
    // off here: it leaves the list — or never enters it.
    if (idx >= 0) {
      countModule(items.value[idx], -1);
      items.value = items.value.filter((item) => item.id !== row.id);
      unreadCount.value = Math.max(0, unreadCount.value - 1);
    }
    return;
  }
  if (idx >= 0) {
    items.value[idx] = { ...items.value[idx], ...row };
  } else {
    items.value = [row, ...items.value];
    unreadCount.value += 1;
    countModule(row, 1);
  }
  items.value = sortItems(items.value);
}

function normalizePayload(payload: unknown): MasterNotification | null {
  if (!payload || typeof payload !== "object") return null;
  const row = payload as Record<string, unknown>;
  const id = Number(row.id ?? 0);
  if (!id) return null;
  return { ...row, id } as unknown as MasterNotification;
}

export async function loadNotifications(): Promise<void> {
  if (listInFlight) return listInFlight;
  if (!hasAuthCredentials()) {
    items.value = [];
    unreadCount.value = 0;
    unreadByModule.value = {};
    hasMore.value = false;
    cursorRow = null;
    loaded.value = true;
    return;
  }
  loading.value = true;
  listInFlight = (async () => {
    try {
      // A page still loading lands first and counts into the length below:
      // the reload neither takes those rows back nor races their append (a
      // page waits for a running reload the same way).
      if (moreInFlight) await moreInFlight.catch(() => undefined);
      // A reload keeps as many rows as are loaded, page by page (master caps
      // one request at 200), so a push or a poll does not cut a list the
      // user has paged.
      let unread = unreadCount.value;
      const { rows, more } = await fetchLoadedRange<MasterNotification>(
        Math.max(PAGE_SIZE, items.value.length),
        MAX_PAGE_SIZE,
        async (limit, after) => {
          const page = await fetchNotificationPage({
            unread: true,
            after,
            limit,
          });
          unread = page.unreadCount;
          return page.notifications;
        },
      );
      items.value = sortItems(rows.filter((row) => !row.readAt));
      cursorRow = rows[rows.length - 1] ?? null;
      unreadCount.value = unread;
      hasMore.value = more;
      loaded.value = true;
      error.value = null;
      // The list answers the total only: ask for the modules once the two
      // disagree (a change this tab did not make).
      if (moduleTotal() !== unread) void refreshUnreadCounts();
    } catch (err) {
      error.value = err instanceof Error ? err.message : "notifications failed";
    } finally {
      loading.value = false;
      listInFlight = null;
    }
  })();
  return listInFlight;
}

/**
 * Appends the next page of unread notifications (master's keyset cursor).
 * A failure rejects and hasMore stays: NxLoadMore offers a retry, and its
 * load on scrolling backs off instead of asking again at once.
 */
export function loadMoreNotifications(): Promise<void> {
  if (moreInFlight) return moreInFlight;
  moreInFlight = (async () => {
    try {
      // A reload in flight replaces the list: page on from its result.
      if (listInFlight) await listInFlight;
      const last = cursorRow;
      if (!hasMore.value || !last) return;
      const params = new URLSearchParams({
        unread: "1",
        limit: String(PAGE_SIZE),
        beforeID: String(last.id),
      });
      const cursor = sortStamp(last);
      if (cursor) params.set("beforeNotifyAt", cursor);
      const company = await requestCompany();
      if (company) params.set("companyID", String(company));
      const res = await request(`/notifications?${params}`);
      const data = (await res.json()) as ListResponse;
      const rows = data.notifications ?? [];
      cursorRow = rows[rows.length - 1] ?? cursorRow;
      const known = new Set(items.value.map((item) => item.id));
      const fresh = rows.filter((row) => !row.readAt && !known.has(row.id));
      items.value = sortItems([...items.value, ...fresh]);
      hasMore.value = rows.length >= PAGE_SIZE;
    } finally {
      moreInFlight = null;
    }
  })();
  return moreInFlight;
}

async function loadUnreadCount(
  wait = 0,
  signal?: AbortSignal,
): Promise<number> {
  const params = new URLSearchParams();
  if (wait > 0) params.set("wait", String(wait));
  // The badge counts what the list shows: the session's company.
  const company = await requestCompany();
  if (company) params.set("companyID", String(company));
  const query = params.toString();
  const res = await request(
    `/notifications/unread-count${query ? `?${query}` : ""}`,
    { signal },
  );
  const data = (await res.json()) as CountResponse;
  // An answer without the modules (a master of before them) keeps what is known.
  modulesAnswered = !!data.modules && typeof data.modules === "object";
  if (modulesAnswered) {
    const counts: Record<string, number> = {};
    for (const [key, value] of Object.entries(data.modules ?? {})) {
      const n = Number(value);
      if (n > 0) counts[key] = n;
    }
    unreadByModule.value = counts;
  }
  return Number(data.count ?? 0);
}

function moduleTotal(): number {
  return Object.values(unreadByModule.value).reduce((sum, n) => sum + n, 0);
}

/** master's last count answer carried the modules; null before the first. */
let modulesAnswered: boolean | null = null;
let countsInFlight: Promise<void> | null = null;

/**
 * Reads the per-module counts once (callers share one request). The total
 * stays the caller's: the list that asked has it as fresh.
 */
function refreshUnreadCounts(): Promise<void> {
  if (modulesAnswered === false) return Promise.resolve();
  if (!countsInFlight) {
    countsInFlight = loadUnreadCount()
      // The next poll or push brings them.
      .then(
        () => undefined,
        () => undefined,
      )
      .finally(() => {
        countsInFlight = null;
      });
  }
  return countsInFlight;
}

function startPolling(): void {
  if (pollRunning) return;
  pollRunning = true;
  void pollUnreadCount(pollGen);
}

function stopPolling(): void {
  pollGen += 1;
  pollRunning = false;
  if (pollTimer) clearTimeout(pollTimer);
  pollTimer = null;
  pollAbort?.abort();
  pollAbort = null;
}

/**
 * A long poll holds one of the few connections a browser keeps to a host (six
 * over HTTP/1.1, for all its windows together): with six windows waiting, every
 * other request of every window would wait with them. So one window of the
 * browser waits; the others ask now and then, and all have the push lane.
 */
function withLongPollSlot<T>(run: (mayWait: boolean) => Promise<T>): Promise<T> {
  const locks = typeof navigator === "undefined" ? undefined : navigator.locks;
  if (!locks) return run(true);
  return locks.request(
    "formica.notifications.long-poll",
    { ifAvailable: true },
    (slot) => run(slot !== null),
  ) as Promise<T>;
}

async function pollUnreadCount(gen: number): Promise<void> {
  if (gen !== pollGen || consumers === 0 || !hasAuthCredentials()) {
    pollRunning = false;
    return;
  }
  const abort = new AbortController();
  pollAbort = abort;
  let waited = false;
  try {
    const next = await withLongPollSlot((mayWait) => {
      waited = mayWait && unreadCount.value === 0;
      return loadUnreadCount(waited ? 60 : 0, abort.signal);
    });
    if (next !== unreadCount.value) {
      unreadCount.value = next;
      void loadNotifications();
    }
  } catch {
    // Transient failures are covered by the next poll/socket event.
  } finally {
    if (pollAbort === abort) pollAbort = null;
    if (gen === pollGen && consumers > 0) {
      pollTimer = setTimeout(
        () => void pollUnreadCount(gen),
        waited && unreadCount.value === 0 ? 1000 : 30000,
      );
    } else if (gen === pollGen) {
      pollRunning = false;
    }
  }
}

/**
 * Marks one read. The bell drops it at once; a failure reloads the bell and
 * rejects, so the notifications page can take its own change back.
 */
export async function markNotificationRead(id: number): Promise<void> {
  dropFromBell([id]);
  try {
    await request(`/notifications/${id}/read`, { method: "POST" });
    unreadCount.value = await loadUnreadCount();
  } catch (err) {
    void loadNotifications();
    throw err;
  }
}

/** Marks several read in one request (master's bulk read); returns how many changed. */
export async function markNotificationsRead(ids: number[]): Promise<number> {
  if (!ids.length) return 0;
  dropFromBell(ids);
  try {
    let marked = 0;
    for (const chunk of chunks(ids, MAX_PAGE_SIZE)) {
      const res = await request("/notifications/read", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notificationIDs: chunk }),
      });
      marked += Number(((await res.json()) as { marked?: number }).marked ?? 0);
    }
    return marked;
  } finally {
    void loadNotifications();
  }
}

/** `?companyID=<the session's company>` for a write that covers a whole inbox; empty without a company. */
async function companyQuery(): Promise<string> {
  const company = await requestCompany();
  return company ? `?companyID=${company}` : "";
}

/**
 * Marks every notification read — every one the bell and the page show: the
 * session's company's and the ones of no company. A failure reloads the
 * bell and rejects.
 */
export async function markAllNotificationsRead(): Promise<void> {
  items.value = [];
  unreadCount.value = 0;
  unreadByModule.value = {};
  hasMore.value = false;
  try {
    await request(`/notifications/read-all${await companyQuery()}`, {
      method: "POST",
    });
  } catch (err) {
    void loadNotifications();
    throw err;
  }
}

/** Splits ids into master's bulk size (200 per request). */
function chunks(ids: number[], size: number): number[][] {
  const out: number[][] = [];
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
  return out;
}

// ── The notifications page (all notifications, read or not) ──────────────

export interface NotificationPageQuery {
  unread?: boolean;
  moduleID?: number;
  /** One notification type (`mail.received`). */
  type?: string;
  /**
   * What is not visible yet — scheduled or snoozed — instead of the inbox,
   * sorted like it: the one that comes last first.
   */
  scheduled?: boolean;
  /** The last row of the previous page: master's keyset cursor (`notifyAt` + id). */
  after?: MasterNotification;
  limit?: number;
}

/** One page of the caller's notifications for the notifications page. */
export async function fetchNotificationPage(
  query: NotificationPageQuery = {},
): Promise<{ notifications: MasterNotification[]; unreadCount: number }> {
  const params = new URLSearchParams({
    limit: String(query.limit ?? PAGE_SIZE),
  });
  if (query.unread) params.set("unread", "1");
  if (query.moduleID) params.set("moduleID", String(query.moduleID));
  if (query.type) params.set("type", query.type);
  if (query.scheduled) params.set("scheduled", "1");
  const cursor = query.after ? sortStamp(query.after) : "";
  if (query.after && cursor) {
    params.set("beforeID", String(query.after.id));
    params.set("beforeNotifyAt", cursor);
  }
  // Only the session's company (and what belongs to none) — the inbox and
  // what is put off alike.
  const company = await requestCompany();
  if (company) params.set("companyID", String(company));
  const res = await request(`/notifications?${params}`);
  const data = (await res.json()) as ListResponse;
  return {
    notifications: data.notifications ?? [],
    // Counted with the list's own filter: the company's unread.
    unreadCount: Number(data.unreadCount ?? 0),
  };
}

/** Marks one read notification unread again; the bell lists it again. */
export async function markNotificationUnread(id: number): Promise<void> {
  await request(`/notifications/${id}/unread`, { method: "POST" });
  void loadNotifications();
}

/**
 * Deletes the caller's notifications by id; returns how many went. Several
 * go in requests of up to 200 ids (master's bulk limit).
 */
export async function deleteNotifications(ids: number[]): Promise<number> {
  if (!ids.length) return 0;
  let deleted = 0;
  if (ids.length === 1) {
    const res = await request(`/notifications/${ids[0]}`, { method: "DELETE" });
    deleted = Number(((await res.json()) as { deleted?: number }).deleted ?? 0);
  } else {
    for (const chunk of chunks(ids, MAX_PAGE_SIZE)) {
      const res = await request("/notifications/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notificationIDs: chunk }),
      });
      deleted += Number(
        ((await res.json()) as { deleted?: number }).deleted ?? 0,
      );
    }
  }
  dropFromBell(ids);
  void loadNotifications();
  return deleted;
}

/**
 * Deletes every read notification the page shows — the session's company's
 * and the ones of no company; unread ones stay.
 */
export async function deleteReadNotifications(): Promise<number> {
  const res = await request(
    `/notifications/delete-read${await companyQuery()}`,
    { method: "POST" },
  );
  const data = (await res.json()) as { deleted?: number };
  void loadNotifications();
  return Number(data.deleted ?? 0);
}

/** Deleted notifications leave the bell's list (and its count, if unread). */
function dropFromBell(ids: number[]): void {
  const gone = new Set(ids);
  for (const listener of dropListeners) listener(ids);
  const before = items.value.length;
  for (const item of items.value) {
    if (gone.has(item.id)) countModule(item, -1);
  }
  items.value = items.value.filter((item) => !gone.has(item.id));
  unreadCount.value = Math.max(
    0,
    unreadCount.value - (before - items.value.length),
  );
}

// ── Remind me later, actions, the type register (I-2915) ─────────────────

/**
 * Hides a notification until `until` on every device: it leaves the inbox
 * and the unread count and comes back unread at that time. The bell drops it
 * at once; a failure reloads the bell and rejects. Null when master did not
 * snooze it (not the caller's, already dealt with).
 */
export async function snoozeNotification(
  id: number,
  until: Date,
): Promise<MasterNotification | null> {
  dropFromBell([id]);
  try {
    const res = await request(`/notifications/${id}/snooze`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ until: until.toISOString() }),
    });
    const data = (await res.json()) as RowResponse;
    void loadNotifications();
    return data.notification ?? null;
  } catch (err) {
    void loadNotifications();
    throw err;
  }
}

/**
 * Takes a snooze back: the notification returns to the inbox now. Null when
 * it was not snoozed (any more).
 */
export async function unsnoozeNotification(
  id: number,
): Promise<MasterNotification | null> {
  const res = await request(`/notifications/${id}/snooze`, {
    method: "DELETE",
  });
  const data = (await res.json()) as RowResponse;
  void loadNotifications();
  return data.notification ?? null;
}

/**
 * Reports one of a notification's actions as done (its request succeeded,
 * its link was opened): the notification becomes resolved with that action
 * and read, and offers no actions any more on any device. Null when master
 * changed nothing — already resolved, e.g. by the module itself.
 */
export async function completeNotification(
  id: number,
  actionKey: string,
): Promise<MasterNotification | null> {
  const res = await request(`/notifications/${id}/complete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ actionKey }),
  });
  const data = (await res.json()) as RowResponse;
  // Resolved means read: it leaves the bell.
  dropFromBell([id]);
  void loadNotifications();
  return data.notification ?? null;
}

/** master's notification type register: what the settings build their switches from. */
export async function fetchNotificationTypes(): Promise<NotificationType[]> {
  const res = await request("/notifications/types");
  const data = (await res.json()) as { types?: NotificationType[] };
  return Array.isArray(data.types) ? data.types : [];
}

/**
 * The session moved to another company without a reload: read which one,
 * forget the former company's rows and counts, and load the new one's.
 * The long poll that still asks for the former company is replaced.
 */
async function followCompanyChange(): Promise<void> {
  // What the store was scoped to when the event came: a request that runs
  // meanwhile may already have taken the new company over.
  const before = scopeCompany;
  const next = await sessionCompanyID(true).catch(() => null);
  if (next === null || next === before) return;
  // A load on its way answers for the former company: let it land first.
  if (listInFlight) await listInFlight.catch(() => undefined);
  scopeCompany = next;
  items.value = [];
  unreadCount.value = 0;
  unreadByModule.value = {};
  hasMore.value = false;
  cursorRow = null;
  if (consumers > 0) {
    stopPolling();
    startPolling();
  }
  await loadNotifications();
}

function onCompanyChanged(): void {
  void followCompanyChange();
}

function handleRealtime(payload: unknown, due: boolean): void {
  const row = normalizePayload(payload);
  // Before the session's company is known nothing is taken from a push:
  // the reload below brings what belongs here.
  if (row && scopeCompany !== null) upsert(row, due);
  void loadNotifications();
}

/**
 * Makes the caller a consumer of the store: the first one starts the long
 * poll and the company listener, each one follows master's push lane, and
 * the returned function ends it (the last one stops the poll). The bell
 * (`useMasterNotifications`) and the apps' notification channel
 * (`notificationChannel.ts`) consume through it.
 */
export function attachNotificationConsumer(
  socket: ReturnType<typeof useNxSocket>,
): () => void {
  const unsubs: Array<() => void> = [];
  consumers += 1;
  // One listener for the store, whoever consumes it.
  if (consumers === 1) {
    window.addEventListener(COMPANY_CHANGED_EVENT, onCompanyChanged);
  }
  void loadNotifications();
  startPolling();
  // master's shared LocalMode push lane: every server-originated push for
  // this account rides "master", with its kind namespaced in evt. Only
  // notification.* frames concern us — ignore anything else so future
  // pushes on the same channel don't get mistaken for notifications.
  const channel = socket?.channel("master");
  if (channel) {
    unsubs.push(
      channel.on("event", (env) => {
        if (env?.evt === "notification.new") {
          // Visible now, master says — also a scheduled or snoozed one
          // whose time has come.
          handleRealtime(env.msg, true);
        } else if (env?.evt === "notification.update") {
          // A change, which may be a snooze: such a row leaves the list.
          handleRealtime(env.msg, false);
        } else if (
          env?.evt === "notification.delete" ||
          env?.evt === "notification.read"
        ) {
          // Deleted, or read in bulk, on the notifications page (any tab):
          // drop, then recount.
          const ids = (env.msg as { ids?: unknown } | undefined)?.ids;
          if (Array.isArray(ids)) dropFromBell(ids.map(Number));
          void loadNotifications();
        } else if (env?.evt === "notification.read_all") {
          items.value = [];
          unreadCount.value = 0;
          unreadByModule.value = {};
          hasMore.value = false;
          void loadNotifications();
        }
      }),
    );
  }

  let attached = true;
  return () => {
    if (!attached) return;
    attached = false;
    while (unsubs.length) unsubs.pop()?.();
    consumers = Math.max(0, consumers - 1);
    // Consumers overlap (a remount mounts the new one first) — only the LAST
    // consumer tears the shared poller down.
    if (consumers === 0) {
      stopPolling();
      window.removeEventListener(COMPANY_CHANGED_EVENT, onCompanyChanged);
    }
  };
}

/** The bell's list (unread, due, newest first), read-only. */
export const bellNotifications = computed<readonly MasterNotification[]>(
  () => items.value,
);

/** Master has more unread notifications than the bell's list holds. */
export const bellHasMore = computed(() => hasMore.value);

const dropListeners = new Set<(ids: readonly number[]) => void>();

/**
 * Calls `listener` with the ids of every notification that leaves the bell
 * (read, snoozed, deleted, here or on another device), so a holder of rows
 * beyond the bell's list drops them at once as well. Returns the function
 * that ends it.
 */
export function onNotificationsDropped(
  listener: (ids: readonly number[]) => void,
): () => void {
  dropListeners.add(listener);
  return () => void dropListeners.delete(listener);
}

export function useMasterNotifications() {
  const socket = useNxSocket();
  let detach: (() => void) | null = null;

  onMounted(() => {
    detach = attachNotificationConsumer(socket);
  });

  onBeforeUnmount(() => {
    detach?.();
    detach = null;
  });

  return {
    items: computed(() => items.value),
    unreadCount: computed(() => unreadCount.value),
    /** Unread per producing module (module key → count; master's own under `master`). */
    unreadByModule: computed(() => unreadByModule.value),
    loading: computed(() => loading.value),
    loaded: computed(() => loaded.value),
    error: computed(() => error.value),
    hasMore: computed(() => hasMore.value),
    refresh: loadNotifications,
    loadMore: loadMoreNotifications,
    // The bell needs no failure: the store has reloaded it already.
    markRead: (id: number) => markNotificationRead(id).catch(() => undefined),
    markAllRead: () => markAllNotificationsRead().catch(() => undefined),
    /** „Später erinnern“: false when it failed (the store has reloaded the bell). */
    snooze: (id: number, until: Date) =>
      snoozeNotification(id, until).then(
        () => true,
        () => false,
      ),
  };
}
